'use client';

import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { BANK_ID_DEVICES, BankIdAuthStatusType, BankIdHintCodeType, NO_DEVICE, UNDEFINED } from '@constants/bankid';

type BankIdSignContextType = {
  currentStatus: BankIdAuthStatusType;
  hintCode: BankIdHintCodeType;
  qrData: string;
  bankIdDevice: BANK_ID_DEVICES;
  setCurrentStatus: (status: BankIdAuthStatusType) => void;
  setHintCode: (hintCode: BankIdHintCodeType) => void;
  setQrData: (qrData: string) => void;
  setBankIdDevice: (bankIdDevice: BANK_ID_DEVICES) => void;
};

const BankIdSignContext = createContext<BankIdSignContextType>({
  currentStatus: UNDEFINED,
  hintCode: UNDEFINED,
  qrData: '',
  bankIdDevice: NO_DEVICE,
  setCurrentStatus: () => {
    /* Overwritten by provider */
  },
  setHintCode: () => {
    /* Overwritten by provider */
  },
  setQrData: () => {
    /* Overwritten by provider */
  },
  setBankIdDevice: () => {
    /* Overwritten by provider */
  },
});

interface Props {
  children: ReactNode;
}

export const BankIdSignContextProvider = ({ children }: Props) => {
  const [currentStatus, setCurrentStatus] = useState<BankIdAuthStatusType>(UNDEFINED);
  const [hintCode, setHintCode] = useState<BankIdHintCodeType>(UNDEFINED);
  const [qrData, setQrData] = useState<string>('');
  const [bankIdDevice, setBankIdDevice] = useState<BANK_ID_DEVICES>(NO_DEVICE);

  const contextValue = useMemo(
    () => ({
      currentStatus,
      setCurrentStatus,
      hintCode,
      setHintCode,
      qrData,
      setQrData,
      bankIdDevice,
      setBankIdDevice,
    }),
    [currentStatus, hintCode, qrData, bankIdDevice]
  );

  return <BankIdSignContext.Provider value={contextValue}>{children}</BankIdSignContext.Provider>;
};

export const useBankIdSignContext = () => useContext(BankIdSignContext);
